/**
 * SecureShield 365 — API Client
 * Talks to the Azure Function backend.
 * In production, /api/ is proxied through the Static Web App.
 */

import { getAccessToken } from './getAccessToken';
import { localDateString } from './dates';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';
const RETRY_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * fetch() wrapper that attaches an Entra access token as
 * `Authorization: Bearer …` when one is available. Falls back to a plain
 * unauthenticated fetch when MSAL is not configured or no user is signed in.
 */
async function authedFetch(url, options = {}) {
  const token = await getAccessToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

// ── Reports ──
export async function fetchLatestReport(slug, scanId = null) {
  // When scanId is provided, fetch that specific historical scan;
  // otherwise fetch the currently-published latest.
  const url = scanId
    ? `${API_BASE}/report/${slug}?scan_id=${encodeURIComponent(scanId)}`
    : `${API_BASE}/report/${slug}`;
  const res = await authedFetch(url);
  if (!res.ok) {
    // Attach status + parsed body (fetchTicketDashboard pattern) so the
    // roster gate's 403 roster_inactive can drive page-level copy. The
    // message format is preserved verbatim — the dashboard's no-data
    // detection matches on "404" in the message.
    const body = await res.json().catch(() => ({}));
    const error = new Error(`Failed to fetch report: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

export async function fetchScans(slug) {
  const res = await authedFetch(`${API_BASE}/clients/${slug}/scans`);
  if (!res.ok) throw new Error(`Failed to fetch scans: ${res.status}`);
  return res.json();
}

export async function fetchAutoRenewRequest(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_auto_renew_request/${encodeURIComponent(slug)}`,
  );
  if (!res.ok) throw new Error(`Auto-renew request unavailable: ${res.status}`);
  return res.json();
}

export async function fetchAutoRenewInternalDisplay(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_auto_renew_internal_display/${encodeURIComponent(slug)}`,
  );
  if (!res.ok) throw new Error(`Auto-renew display unavailable: ${res.status}`);
  return res.json();
}

export async function submitAutoRenewRequest(slug, offerPrecondition) {
  if (!offerPrecondition) throw new Error("Auto-renew offer precondition missing");
  const res = await authedFetch(
    `${API_BASE}/portal_auto_renew_request/${encodeURIComponent(slug)}`,
    { method: "POST", headers: { "Content-Type": "application/json", "If-Match": offerPrecondition }, body: "{}" },
  );
  if (!res.ok) {
    const error = new Error(`Auto-renew request status unavailable: ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function archiveReport(slug, scoredPath, reason, archiveRequestId, identity) {
  if (!archiveRequestId || !identity?.sha256 || identity.scored_path !== scoredPath) throw new Error("Archive target and request ID required");
  const res = await authedFetch(`${API_BASE}/portal_report_archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, scored_path: scoredPath, identity, reason: reason || null, archive_request_id: archiveRequestId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Archive failed: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

export async function restoreReport(slug, fileName, restoreRequestId, identity) {
  if (!restoreRequestId || !identity?.sha256) throw new Error("Restore target and request ID required");
  const res = await authedFetch(`${API_BASE}/portal_report_restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, file_name: fileName, identity, restore_request_id: restoreRequestId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Restore failed: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

export async function fetchArchivedReports(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_report_archived?slug=${encodeURIComponent(slug)}`
  );
  if (!res.ok) throw new Error(`Failed to fetch archived scans: ${res.status}`);
  return res.json();
}

export async function unpublishReport(slug, reason, unpublishRequestId, publicationId, identity) {
  if (!unpublishRequestId || !identity?.sha256) throw new Error("Unpublish target and request ID required");
  const res = await authedFetch(`${API_BASE}/portal_report_unpublish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ slug, identity, reason: reason || null, unpublish_request_id: unpublishRequestId, publication_id: publicationId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Un-publish failed: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

export async function fetchRemediated(slug, scanId = null) {
  const url = scanId
    ? `${API_BASE}/clients/${slug}/remediated?scan_id=${encodeURIComponent(scanId)}`
    : `${API_BASE}/clients/${slug}/remediated`;
  const res = await authedFetch(url);
  if (!res.ok) throw new Error(`Failed to fetch remediated: ${res.status}`);
  return res.json();
}

export async function fetchPostureComparison(slug, scanId, mode, comparisonScanId = null) {
  const params = new URLSearchParams({ scan_id: scanId, mode });
  if (comparisonScanId) params.set('comparison_scan_id', comparisonScanId);
  const res = await authedFetch(`${API_BASE}/clients/${slug}/posture-comparison?${params}`);
  if (!res.ok) {
    const error = new Error(`Failed to fetch posture comparison: ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function fetchReportHistory(clientId) {
  const res = await authedFetch(`${API_BASE}/history/${clientId}`);
  if (!res.ok) throw new Error(`Failed to fetch history: ${res.status}`);
  return res.json();
}

// ── Client Info (from Dynamics) ──
export async function fetchClientInfo(clientId) {
  const res = await authedFetch(`${API_BASE}/client/${clientId}`);
  if (!res.ok) throw new Error(`Failed to fetch client: ${res.status}`);
  return res.json();
}

// ── Client List (internal picker) ──
// scope: omitted → the backend default ("entitled" — customers the portal
// serves, per shared/portal_services); "all" → the full pc_customers
// universe (the Patriot "show all customers" escape hatch).
export async function fetchClients({ scope } = {}) {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  const res = await authedFetch(`${API_BASE}/clients${qs}`);
  if (!res.ok) throw new Error(`Failed to fetch clients: ${res.status}`);
  return res.json();
}

// ClientPicker variant: also surfaces the applied scope from the
// X-Portal-Scope header ("entitled" | "all" | "all_fallback" — the last
// meaning the entitlement read failed and the backend failed OPEN to the
// full universe, which the picker labels rather than hiding).
export async function fetchClientsMeta({ scope } = {}) {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  const res = await authedFetch(`${API_BASE}/clients${qs}`);
  if (!res.ok) throw new Error(`Failed to fetch clients: ${res.status}`);
  const clients = await res.json();
  return { clients, scopeApplied: res.headers.get("X-Portal-Scope") };
}

// ── Review queue (Patriot-only): clients with a held scan awaiting publish ──
export async function fetchReviewQueue() {
  const res = await authedFetch(`${API_BASE}/review-queue`);
  if (!res.ok) throw new Error(`Failed to fetch review queue: ${res.status}`);
  return res.json();
}

// ── Identity (authoritative resolved slug for the signed-in user) ──
// Returns { email, tenant_id, is_patriot, slug, customer_name }. The slug
// here is the single source of truth for which customer's data to load —
// it must not be inferred from the raw token tid (server-side rules like the
// Demo.Only pin can rewrite the effective tenant after authentication).
export async function fetchMe() {
  const res = await authedFetch(`${API_BASE}/me`);
  if (!res.ok) throw new Error(`Failed to fetch identity: ${res.status}`);
  return res.json();
}

// ── Entitlements (active services for a tenant) ──
export async function fetchEntitlements(tenantId) {
  const res = await authedFetch(`${API_BASE}/entitlements/${encodeURIComponent(tenantId)}`);
  if (!res.ok) throw new Error(`Failed to fetch entitlements: ${res.status}`);
  return res.json();
}

// ── Service inquiry (upsell "Request info" submissions) ──
// Sends ONLY { service, message }. Identity (name/email/company/tenant) is
// derived server-side from the validated token — never trust client-supplied
// identity here. Throws on non-ok so the caller can surface an error state.
export async function submitServiceInquiry({ service, message }) {
  const res = await authedFetch(`${API_BASE}/submit_service_inquiry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ service, message }),
  });
  if (!res.ok) throw new Error(`submitServiceInquiry failed: ${res.status}`);
  return res.json();
}

// ── Freshservice Tickets ──
export async function createTicket(payload) {
  const res = await authedFetch(`${API_BASE}/create_ticket`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Ticket creation failed: ${res.status}`);
  return res.json();
}

export async function fetchClientTickets(clientId) {
  const res = await authedFetch(`${API_BASE}/tickets/${clientId}`);
  if (!res.ok) throw new Error(`Failed to fetch tickets: ${res.status}`);
  return res.json();
}

// Support & Hours dashboard — ticket history + support-hours rollup.
// Returns { slug, department_id, tier, tickets:[...], settings, l3 }.
// fresh=true (post-submit refetch only) asks the backend to skip its
// per-instance memory layer and read the shared blob cache directly, so a
// just-injected ticket can't be hidden by another instance's stale L1.
export async function fetchTicketDashboard(slug, fresh = false) {
  const url = `${API_BASE}/ticket_dashboard/${encodeURIComponent(slug)}${fresh ? "?fresh=1" : ""}`;
  const res = await authedFetch(url);
  if (!res.ok) {
    // Attach the parsed body — the domain-gate codes (no_department_for_slug /
    // no_domains_on_file / domain_mismatch) drive self-explaining error copy.
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `Failed to fetch ticket dashboard: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Company domains for soft-validating an on-behalf requester email (internal use).
// Returns { domains: [...] }.
export async function fetchCompanyDomains(slug) {
  const res = await authedFetch(`${API_BASE}/company_domains/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error(`Failed to fetch company domains: ${res.status}`);
  return res.json();
}

// ── Portal Access (Company Profile page) ──
export async function fetchAccessList(slug) {
  const res = await authedFetch(`${API_BASE}/access_list/${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error(`Failed to fetch access list: ${res.status}`);
  return res.json();
}

// Minimal customer-scoped scan-tool read for the Company Profile upgrade
// section. The backend returns only {scan_tool_key, has_tracking_row}.
export async function fetchUpgradeStatus(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_upgrade_status?slug=${encodeURIComponent(slug)}`
  );
  if (!res.ok) throw new Error(`Failed to fetch upgrade status: ${res.status}`);
  return res.json();
}

// Pending access requests across ALL customers (Patriot-only; 403 otherwise).
// Returns { pending: [...] }, oldest first.
export async function fetchAdminPendingAccess() {
  const res = await authedFetch(`${API_BASE}/portal_pending_access`);
  if (!res.ok) throw new Error(`Failed to fetch pending access: ${res.status}`);
  return res.json();
}

// Per-customer login rollup for the Admin Logins tab (Patriot-only; 403
// otherwise). Returns { customers: [{slug, customer_name, most_recent_login,
// active_user_count}] }, most recent login first. Served from a ~120s
// backend cache.
// scope: omitted → backend default ("entitled" ∪ lapsed-with-active);
// "all" → every pc_customers row. Payload carries scope_applied
// ("entitled" | "all" | "all_fallback") so the tab can label the
// fail-open case.
export async function fetchAdminLoginSummary({ scope } = {}) {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : "";
  const res = await authedFetch(`${API_BASE}/portal_login_summary${qs}`);
  if (!res.ok) throw new Error(`Failed to fetch login summary: ${res.status}`);
  return res.json();
}

// Per-customer scan-ops rollup for the Admin Scanning tab (Patriot-only;
// 403 otherwise). Returns { customers: [{slug, customer_name, tenant_id,
// domains, scan_tool, scan_tool_key, tracking_id, auth_method,
// auth_method_key, migration_date, fabric_admin, hybrid_admin, has_notes}] },
// Unclassified first then alphabetical. Served from a ~120s backend cache.
export async function fetchAdminScanSummary() {
  const res = await authedFetch(`${API_BASE}/portal_scan_summary?scope=worklist`);
  if (!res.ok) throw new Error(`Failed to fetch scan summary: ${res.status}`);
  return res.json();
}

// RETIRED 2026-08-08 — the Delivery Ops tab was removed and this has no
// caller. Kept, unused, alongside its backend route so a revert is one-sided.
// Read-only Delivery Ops queue for the admin Delivery Ops tab (Patriot-only;
// 403 otherwise). Returns { rows: [{customer_name, sequence_number,
// cycle_status, target_date, scan_status, preflight_outcome, ...}] }, one row
// per review cycle joined with its latest scan. Served from a ~120s backend
// cache. Empty against the DEV Dataverse org — the tables are prod-only.
export async function fetchDeliveryQueue() {
  const res = await authedFetch(`${API_BASE}/portal_delivery_queue`);
  if (!res.ok) throw new Error(`Failed to fetch delivery queue: ${res.status}`);
  return res.json();
}

// Read-only review-ops rollup for the Admin Reviews tab (Patriot-only; 403
// otherwise). Returns { customers: [{slug, customer_name, next_audit_date,
// bucket, days_overdue, days_until, contract_year, review_number, tier,
// contract_status, workflow_status, workflow_status_value, last_review}],
// by_engineer: [...], volume: {...}, range: {start, end},
// meta: {today, backlog_grace_days, approaching_days,
// any_completions_recorded, customer_count} }.
//
// `today` is the OPERATOR's local date, not the server's UTC — this view is
// entirely day counts, and after ~18:00 US-Central the server has already
// rolled over. The backend validates it within +/-1 day either way (the
// click_date guard from the Review Complete button).
//
// start/end bound the by-engineer and volume views only; the customer list is
// range-independent and served from a ~120s backend cache.
export async function fetchReviewOps({ start, end, today } = {}) {
  const qs = new URLSearchParams();
  qs.set("today", today || localDateString());
  if (start) qs.set("start", start);
  if (end) qs.set("end", end);
  const res = await authedFetch(`${API_BASE}/portal_review_ops?${qs}`);
  if (!res.ok) throw new Error(`Failed to fetch review ops: ${res.status}`);
  return res.json();
}

// Patriot-only Customer Operations Notes for one server-selected Review Ops
// Tracking row. The opaque route selector is validated again by the backend;
// callers never send a customer id, title, actor, sort, or Dataverse path.
export async function fetchCustomerOperationsNotes(trackingId) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_operations_notes/${encodeURIComponent(trackingId)}`
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(
      body.error || `Customer Operations Notes are unavailable: ${res.status}`
    );
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

export async function createCustomerOperationsNote(trackingId, noteText) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_operations_notes/${encodeURIComponent(trackingId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note_text: noteText.trim() }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(
      body.error || `Customer Operations Note creation failed: ${res.status}`
    );
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

export async function markCustomerOperationsNoteHandled(trackingId, handleRef) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_operations_notes/${encodeURIComponent(trackingId)}`,
    { method:"POST", headers:{ "Content-Type":"application/json" }, body:JSON.stringify({ handle_ref:handleRef }) },
  );
  if (!res.ok) throw new Error(`Customer Operations Note handling failed: ${res.status}`);
  return res.json();
}

// The weekly management report payload (#547): customer_management,
// scheduling, delivery, scan_fleet and meta in one object. Patriot-only.
//
// This exists because the endpoint needs the app's Bearer token — pasting
// the URL into a browser tab returns 401, which is why the report was
// being fetched by copying a token out of DevTools. The Assessments tab
// downloads it through this instead. Range params are deliberately not
// exposed: the button is "give me this week", and the endpoint's own
// default (the trailing 7 days) is that answer.
export async function fetchWeeklyReport() {
  const res = await authedFetch(`${API_BASE}/portal_weekly_report`);
  if (!res.ok) throw new Error(`Failed to fetch weekly report: ${res.status}`);
  return res.json();
}

// Separate from portal_weekly_report: this is the prepared, content-minimized
// Freshservice snapshot. It never asks the backend to refresh Freshservice.
export async function fetchFreshserviceWeeklyReportStatus() {
  const res = await authedFetch(`${API_BASE}/portal_freshservice_weekly_report`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(`Failed to fetch Freshservice weekly report: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

export async function downloadFreshserviceWeeklyReport(period) {
  const res = await authedFetch(
    `${API_BASE}/portal_freshservice_weekly_report_download?period=${encodeURIComponent(period)}`
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(`Failed to download Freshservice weekly report: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.blob();
}

export async function fetchFreshserviceGroupDiscovery() {
  const res = await authedFetch(`${API_BASE}/portal_freshservice_group_discovery`);
  if (!res.ok) throw new Error(`Group discovery status failed: ${res.status}`);
  return res.json();
}

async function freshserviceExportRequest(route, options, download = false) {
  const res = await authedFetch(`${API_BASE}/portal_freshservice_export_${route}`, options);
  if (!res.ok) {
    const error = new Error("Freshservice export unavailable");
    error.status = res.status;
    throw error;
  }
  return download ? res.blob() : res.json();
}

export const fetchFreshserviceExportStatus = () => freshserviceExportRequest("status");
export const startFreshserviceExport = ({ refresh = false, confirmed = false } = {}) =>
  freshserviceExportRequest("start", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh, confirmed }),
  });
export const downloadFreshserviceExport = () => freshserviceExportRequest("download", undefined, true);

export async function requestFreshserviceGroupDiscovery() {
  const res = await authedFetch(`${API_BASE}/portal_freshservice_group_discovery`, { method:"POST" });
  if (!res.ok) throw new Error(`Group discovery request failed: ${res.status}`);
  return res.json();
}

export async function downloadFreshserviceGroupDiscovery() {
  const res = await authedFetch(`${API_BASE}/portal_freshservice_group_discovery_download`);
  if (!res.ok) throw new Error(`Group discovery download failed: ${res.status}`);
  return res.blob();
}

// READ-ONLY contract-data list for the Assessments section's Customer
// Management view (Patriot-only; 403 otherwise). Returns
// { customers: [{slug, customer_name, contract_status, tier, term_start_date,
// term_number, duration_months, term_end, term_end_display,
// days_to_term_end, contract_year, review_number, reviews_per_year,
// expiration_date, next_audit_date, auto_renew, renewal_state, problems[],
// has_problem, plus one boolean per predicate}],
// meta: {today, customer_count, problems{}, problem_count, problem_order[],
// renewal_approaching_days, term_exempt_count, terminal_row_count} }.
//
// A SEPARATE CALL from fetchReviewOps, not a widened one, because the
// UNIVERSE differs: current clients are the default, while inactive/history
// rows are an explicit operator opt-in for cleanup.
//
// `today` is the OPERATOR's local date: every predicate here is a date
// comparison, and the backend validates it within +/-1 day either way.
//
// THIS FLEET FETCH DOES NOT WRITE. Record Renewal uses a separate read-only
// preflight plus explicit Schedule/Activate/Void action calls below.
export async function fetchCustomerManagement({ today, includeInactive = false } = {}) {
  const qs = new URLSearchParams();
  qs.set("today", today || localDateString());
  if (includeInactive) qs.set("include_inactive", "1");
  const res = await authedFetch(
    `${API_BASE}/portal_customer_management?${qs}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch customer management: ${res.status}`);
  }
  return res.json();
}

// A separate, lazy Patriot-only read: the default Customer Management lens
// stays contract data while cadence loads dated ledger facts only on demand.
export async function fetchReviewCadence({ today, includeInactive = false } = {}) {
  const qs = new URLSearchParams();
  qs.set("today", today || localDateString());
  if (includeInactive) qs.set("include_inactive", "1");
  const res = await authedFetch(
    `${API_BASE}/portal_review_cadence?${qs}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch review cadence: ${res.status}`);
  }
  return res.json();
}

async function allowanceAdjustmentResponse(res, fallback) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.error || `${fallback}: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return body;
}

// Patriot-only, lazy Customer Data card. This is separate from the fleet so
// adjustment reasons, notes, history, and UUIDs stay inside the internal modal
// and never ride customer-facing payloads.
export async function fetchAllowanceAdjustments(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_management/${encodeURIComponent(slug)}/allowance_adjustments`,
  );
  return allowanceAdjustmentResponse(
    res, "Failed to fetch allowance adjustments");
}

export async function previewAllowanceAdjustment(slug, {
  amount,
  reason,
  effectiveDate,
  note,
} = {}) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_management/${encodeURIComponent(slug)}/allowance_adjustments/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amount,
        reason,
        ...(effectiveDate ? { effective_date: effectiveDate } : {}),
        note,
      }),
    },
  );
  return allowanceAdjustmentResponse(
    res, "Failed to preview allowance adjustment");
}

export async function createAllowanceAdjustment(slug, {
  amount,
  reason,
  effectiveDate,
  note,
  idempotencyKey,
  acknowledgedPreviewHash,
} = {}) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_management/${encodeURIComponent(slug)}/allowance_adjustments`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amount,
        reason,
        ...(effectiveDate ? { effective_date: effectiveDate } : {}),
        note,
        idempotency_key: idempotencyKey,
        acknowledged_preview_hash: acknowledgedPreviewHash,
      }),
    },
  );
  return allowanceAdjustmentResponse(
    res, "Failed to create allowance adjustment");
}

// Patriot-only Banked transition flow. Kept separate from the ordinary
// adjustment card because this creates the immutable opening-balance seed and
// flips Tracking to Banked in one backend-owned change set.
export async function fetchAllowanceTransition(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_management/${encodeURIComponent(slug)}/allowance_transition`,
  );
  return allowanceAdjustmentResponse(
    res, "Failed to fetch allowance transition");
}

export async function previewAllowanceTransition(slug, { note } = {}) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_management/${encodeURIComponent(slug)}/allowance_transition/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    },
  );
  return allowanceAdjustmentResponse(
    res, "Failed to preview allowance transition");
}

export async function createAllowanceTransition(slug, {
  transitionId,
  note,
  acknowledgedPreviewHash,
} = {}) {
  const res = await authedFetch(
    `${API_BASE}/portal_customer_management/${encodeURIComponent(slug)}/allowance_transition`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        transition_id: transitionId,
        note,
        acknowledged_preview_hash: acknowledgedPreviewHash,
      }),
    },
  );
  return allowanceAdjustmentResponse(
    res, "Failed to create allowance transition");
}

// Patriot-only preflight for the Customer Management Record Renewal modal.
async function contractChangeResponse(response, action) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error || `Contract Change ${action} failed`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

export async function fetchContractChangeContext(slug, changeType) {
  if (changeType !== undefined && changeType !== "cancel") {
    throw new Error("Unsupported contract context action");
  }
  const query = changeType === "cancel" ? "?changeType=cancel" : "";
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_context/${encodeURIComponent(slug)}${query}`),
  "context");
}

export async function fetchContractChangeReceipt(slug, operationKind, requestId) {
  const params = new URLSearchParams({ operationKind, requestId });
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_receipt/${encodeURIComponent(slug)}?${params}`),
  "receipt");
}

export async function fetchContractChangeOperations(slug) {
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_operations/${encodeURIComponent(slug)}`),
  "history");
}

export async function fetchContractChangeOperationDetail(slug, receiptStateId) {
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_operations/${encodeURIComponent(slug)}/${encodeURIComponent(receiptStateId)}`),
  "receipt");
}

export async function previewContractChange(slug, input) {
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_preview/${encodeURIComponent(slug)}`,
    { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input) }), "preview");
}

export async function confirmContractChange(slug, input) {
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_confirm/${encodeURIComponent(slug)}`,
    { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input) }), "confirmation");
}

export async function previewContractChangeCorrection(slug, input) {
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_correction_preview/${encodeURIComponent(slug)}`,
    { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input) }), "allocation correction preview");
}

export async function confirmContractChangeCorrection(slug, input) {
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_correction_confirm/${encodeURIComponent(slug)}`,
    { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input) }), "allocation correction confirmation");
}

export async function previewContractChangePredecessor(slug, input) {
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_predecessor_preview/${encodeURIComponent(slug)}`,
    { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input) }), "historical review preview");
}

export async function confirmContractChangePredecessor(slug, input) {
  return contractChangeResponse(await authedFetch(
    `${API_BASE}/portal_contract_change_predecessor_confirm/${encodeURIComponent(slug)}`,
    { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input) }), "historical review confirmation");
}

// This GET is still read-only; PR 3 adds separate POST actions below.
export async function fetchRenewalPreview(slug, {
  today,
  lifecycleAction,
  newTermStartDate,
  durationMonths,
  reviewsPerYear,
  proposedTier,
  rescindCancellationEffectiveDate,
} = {}) {
  const qs = new URLSearchParams();
  qs.set("today", today || localDateString());
  if (lifecycleAction) qs.set("lifecycle_action", lifecycleAction);
  if (newTermStartDate) qs.set("new_term_start_date", newTermStartDate);
  if (durationMonths) qs.set("duration_months", durationMonths);
  if (reviewsPerYear) qs.set("reviews_per_year", reviewsPerYear);
  if (proposedTier) qs.set("proposed_tier", proposedTier);
  if (rescindCancellationEffectiveDate) {
    qs.set("rescind_cancellation_effective_date",
      rescindCancellationEffectiveDate);
  }
  const res = await authedFetch(
    `${API_BASE}/portal_record_renewal_preview/${encodeURIComponent(slug)}?${qs}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(
      body.error || `Failed to fetch renewal preview: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

async function postRenewalAction(slug, action, body) {
  const res = await authedFetch(
    `${API_BASE}/portal_record_renewal_${action}/${encodeURIComponent(slug)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    },
  );
  if (!res.ok) {
    const responseBody = await res.json().catch(() => ({}));
    const error = new Error(
      responseBody.error || `Failed to ${action} renewal: ${res.status}`);
    error.status = res.status;
    error.body = responseBody;
    throw error;
  }
  return res.json();
}

export async function scheduleRenewal(slug, {
  lifecycleAction,
  newTermStartDate,
  durationMonths,
  reviewsPerYear,
  proposedTier,
  concurrencyToken,
  idempotencyKey,
  note,
} = {}) {
  return postRenewalAction(slug, "schedule", {
    ...(lifecycleAction ? { lifecycle_action: lifecycleAction } : {}),
    new_term_start_date: newTermStartDate,
    duration_months: durationMonths,
    reviews_per_year: reviewsPerYear,
    proposed_tier: proposedTier,
    ...(concurrencyToken ? { concurrency_token: concurrencyToken } : {}),
    idempotency_key: idempotencyKey,
    ...(note ? { note } : {}),
  });
}

export async function activateRenewal(slug, {
  lifecycleAction,
  newTermStartDate,
  durationMonths,
  reviewsPerYear,
  scheduledEventId,
  concurrencyToken,
  idempotencyKey,
  note,
} = {}) {
  return postRenewalAction(slug, "activate", {
    ...(lifecycleAction ? { lifecycle_action: lifecycleAction } : {}),
    new_term_start_date: newTermStartDate,
    duration_months: durationMonths,
    reviews_per_year: reviewsPerYear,
    scheduled_event_id: scheduledEventId,
    concurrency_token: concurrencyToken,
    confirm_current: true,
    idempotency_key: idempotencyKey,
    ...(note ? { note } : {}),
  });
}

export async function voidRenewal(slug, {
  lifecycleAction,
  scheduledEventId,
  concurrencyToken,
  idempotencyKey,
  reason,
  note,
} = {}) {
  return postRenewalAction(slug, "void", {
    ...(lifecycleAction ? { lifecycle_action: lifecycleAction } : {}),
    scheduled_event_id: scheduledEventId,
    ...(concurrencyToken ? { concurrency_token: concurrencyToken } : {}),
    idempotency_key: idempotencyKey,
    reason,
    ...(note ? { note } : {}),
  });
}

export async function rescindRenewal(slug, {
  activatedEventId,
  cancellationEffectiveDate,
  concurrencyToken,
  idempotencyKey,
  reason,
  note,
} = {}) {
  return postRenewalAction(slug, "rescind", {
    activated_event_id: activatedEventId,
    cancellation_effective_date: cancellationEffectiveDate,
    concurrency_token: concurrencyToken,
    confirm_current: true,
    idempotency_key: idempotencyKey,
    reason,
    ...(note ? { note } : {}),
  });
}

// Patriot-only workflow-status write for the Reviews tab (Phase A.5).
// The caller sends a STATUS, never a decision about what a date means —
// the backend's STATUS_DATE_RULE table decides whether a date is stamped,
// required, or untouched, and refuses any combination it does not sanction.
// `date` is sent ONLY for the two statuses whose rule is require_date.
// Returns the recomputed row (status, next_audit_date, bucket, severity,
// replaced_date) so the caller updates from the server's answer.
// Errors preserve status + parsed body (createAccessRequest idiom).
/** Mark or unmark the survey as sent on ONE completion (Patriot-only).
 *
 *  `clear: true` is the undo and wins over any supplied date — the server
 *  never infers the direction from a value the client read a minute ago. An
 *  omitted `date` means today, resolved server-side against the caller's
 *  validated today. */
export async function setSurveySent({ completionId, date, clear, today }) {
  const res = await authedFetch(`${API_BASE}/portal_review_survey`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      completion_id: completionId,
      ...(clear ? { clear: true } : (date ? { date } : {})),
      today: today || localDateString(),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body?.error || `Failed to set survey sent: ${res.status}`);
    err.body = body;
    throw err;
  }
  return body;
}

export async function setReviewStatus({ slug, statusValue, date, today }) {
  // An uncertain response must retry the same governed operation, including
  // its original date authority. This stores only the opaque operation key
  // and the staff's ordinary status/date choice for this browser tab.
  const key = `ss365-review-status:${slug}`;
  const choice = JSON.stringify({ statusValue, date: date || null });
  let pending = null;
  let hadStored = false;
  let createdSnapshot = null;
  try {
    const stored = window.sessionStorage.getItem(key);
    if (stored !== null) {
      hadStored = true;
      pending = JSON.parse(stored);
    }
  } catch (_) {
    throw new Error("This browser cannot safely remember the status request.");
  }
  if (hadStored && (pending === null ||
    typeof pending !== "object" || Array.isArray(pending) ||
    Object.keys(pending).sort().join(",") !== "choice,requestId,today" ||
    typeof pending.choice !== "string" ||
    typeof pending.requestId !== "string" ||
    !RETRY_UUID.test(pending.requestId) ||
    typeof pending.today !== "string" ||
    !/^\d{4}-\d{2}-\d{2}$/.test(pending.today) ||
    Number.isNaN(Date.parse(`${pending.today}T00:00:00Z`)) ||
    new Date(`${pending.today}T00:00:00Z`).toISOString().slice(0, 10) !== pending.today
  )) {
    throw new Error("Check the previous status result before making another change.");
  }
  if (pending && pending.choice !== choice) {
    throw new Error("Check the previous status result before making another change.");
  }
  if (!pending) {
    if (typeof window.crypto?.randomUUID !== "function") {
      throw new Error("This browser cannot create a safe status request.");
    }
    pending = { choice, requestId: window.crypto.randomUUID(),
      today: today || localDateString() };
    if (!RETRY_UUID.test(pending.requestId)) {
      throw new Error("This browser cannot create a safe status request.");
    }
    try {
      const encoded = JSON.stringify(pending);
      window.sessionStorage.setItem(key, encoded);
      if (window.sessionStorage.getItem(key) !== encoded) {
        throw new Error("status request was not retained");
      }
      createdSnapshot = encoded;
    } catch (_) {
      throw new Error("This browser cannot safely remember the status request.");
    }
  }
  try {
    if (window.sessionStorage.getItem(key) !== JSON.stringify(pending)) {
      throw new Error("status request changed");
    }
  } catch (_) {
    throw new Error("This browser cannot safely remember the status request.");
  }
  const res = await authedFetch(`${API_BASE}/portal_review_status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      slug,
      status_value: statusValue,
      date: date || undefined,
      today: pending.today,
      requestId: pending.requestId,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `setReviewStatus failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    // Only this invocation's first, proven pre-effect refusal releases its key.
    // An older pending operation can still represent an unacknowledged write.
    if (!hadStored && createdSnapshot !== null && res.status === 400 &&
        err !== null && typeof err === "object" && !Array.isArray(err) &&
        Object.keys(err).join(",") === "error" &&
        err.error === "today is too far from the server date — check the device clock") {
      try {
        if (window.sessionStorage.getItem(key) === createdSnapshot) {
          window.sessionStorage.removeItem(key);
        }
      } catch (_) { /* retain uncertainty and throw the original request error */ }
    }
    throw error;
  }
  const result = await res.json();
  try { window.sessionStorage.removeItem(key); } catch (_) { /* unavailable */ }
  return result;
}

// Patriot-only Scan Fleet edit. The backend maps the stable scan-tool key to
// the Dataverse choice integer and appends the note/audit line server-side.
// Errors preserve status + parsed body (createAccessRequest idiom).
export async function updateScanTracking(trackingId, {
  scanToolKey,
  authMethodKey,
  migrationDate,
  fabricAdmin,
  hybridAdmin,
  note,
}) {
  const res = await authedFetch(`${API_BASE}/portal_scan_tracking_update`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      tracking_id: trackingId,
      scan_tool_key: scanToolKey || null,
      auth_method_key: authMethodKey || null,
      migration_date: migrationDate || null,
      fabric_admin: fabricAdmin,
      hybrid_admin: hybridAdmin,
      note: note?.trim() || null,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `updateScanTracking failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

export async function fetchScanTrackingNotes(trackingId) {
  const res = await authedFetch(
    `${API_BASE}/portal_scan_tracking_notes?tracking_id=${encodeURIComponent(trackingId)}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `fetchScanTrackingNotes failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Full portal-access roster across ALL customers with lifecycle/decision
// fields (Patriot-only; 403 otherwise). Returns { rows: [...] }. ~120s
// server cache; pass { fresh: true } to bypass the cache read (used by the
// post-decision refetch so a just-decided row appears immediately).
// scope: omitted → backend default ("entitled" ∪ lapsed-with-active);
// "all" → every roster row. Payload carries entitled_slugs,
// lapsed_active_slugs (null = unavailable) and scope_applied.
export async function fetchAdminAccessLifecycle({ fresh = false, scope } = {}) {
  const params = [];
  if (fresh) params.push("fresh=1");
  if (scope) params.push(`scope=${encodeURIComponent(scope)}`);
  const qs = params.length ? `?${params.join("&")}` : "";
  const res = await authedFetch(`${API_BASE}/portal_access_lifecycle${qs}`);
  if (!res.ok) throw new Error(`Failed to fetch access lifecycle: ${res.status}`);
  return res.json();
}

// ── SecureScan365 scan proxy (Admin → SecureScan365 tab; Patriot-only,
// 403 otherwise) ──
// Backend proxies the SecureScan365 engine server-side (function key never
// reaches the browser). All routes are portal_scan_* — see the backend
// section comment for the reserved-"admin*"-prefix hazard. In prod, a 503
// {"error":"scan_engine_not_configured"} means the engine credentials
// aren't wired up yet (SA_V2_* app settings).

// Stored passkey users able to scan a customer's tenant.
// Returns { users: [{user_principal_name, display_name, secret_name}] }.
export async function fetchScanPasskeys(slug) {
  const res = await authedFetch(`${API_BASE}/portal_scan_passkeys?slug=${encodeURIComponent(slug)}`);
  if (!res.ok) throw new Error(`Failed to fetch stored passkeys: ${res.status}`);
  return res.json();
}

// What the Start Assessment popup shows BEFORE anything is started:
// {slug, customer_name, cap:{capped, last_completed_utc,
// next_available_utc, source}, in_flight, shared_tenant_with[],
// auto_send_ready, auto_send_upn, auto_send_block, last_run_duration_seconds}.
//
// Patriot-only server-side. Read-only — it starts nothing and files no
// ticket, which is the point: discovering a setup gap by firing a blocked
// customer-path start would open a Freshservice ticket per customer.
//
// ADVISORY. The cap it reports is the same derivation the start route's
// 429 uses, but nothing is reserved between the two calls — a start that
// 429s anyway is authoritative and the modal re-renders from it.
export async function fetchScanStartPreflight(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_scan_start_preflight?slug=${encodeURIComponent(slug)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || `Failed to load scan preflight: ${res.status}`);
    error.status = res.status;
    error.body = data;
    throw error;
  }
  return data;
}

export async function fetchAdminScanJourney(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_admin_scan_journey?slug=${encodeURIComponent(slug)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Journey unavailable"), { status: res.status, body: data });
  return data;
}

export async function fetchAdminScanJourneys({ cursor, state = "all", limit = 50 } = {}) {
  const params = new URLSearchParams({ state, limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  const res = await authedFetch(`${API_BASE}/portal_admin_scan_journeys?${params}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Journeys unavailable"), { status: res.status, body: data });
  return data;
}

export async function fetchAdminScanJourneyRecords({ cursor, view = "current", state = "all_open", customer = "", limit = 50 } = {}) {
  const params = new URLSearchParams({ view, state, limit: String(limit) });
  if (customer) params.set("customer", customer);
  if (cursor) params.set("cursor", cursor);
  const res = await authedFetch(`${API_BASE}/portal_admin_scan_journey_records?${params}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Journeys unavailable"), { status: res.status, body: data });
  return data;
}

export async function fetchAdminScanJourneyRecord(journeyId, slug, lookup = {}) {
  const params = new URLSearchParams({ journey_id: journeyId, slug });
  if (lookup.kind) params.set("run_lookup_kind", lookup.kind);
  if (lookup.cursor) params.set("run_lookup_cursor", lookup.cursor);
  const res = await authedFetch(`${API_BASE}/portal_admin_scan_journey_record?${params}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Journey unavailable"), { status: res.status, body: data });
  return data;
}

export async function previewAdminScanJourneyAction(request) {
  const res = await authedFetch(`${API_BASE}/portal_admin_scan_journey_action_preview`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Action unavailable"), { status: res.status, body: data });
  return data;
}

export async function executeAdminScanJourneyAction(request) {
  const res = await authedFetch(`${API_BASE}/portal_admin_scan_journey_action`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Action pending verification"), { status: res.status, body: data });
  return data;
}

export async function fetchAdminScanJourneyActionStatus(operationId, journeyId, slug) {
  const params = new URLSearchParams({ operation_id: operationId });
  if (journeyId) params.set("journey_id", journeyId);
  if (slug) params.set("slug", slug);
  const res = await authedFetch(`${API_BASE}/portal_admin_scan_journey_action_status?${params}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Action status unavailable"), { status: res.status, body: data });
  return data;
}

export async function previewAdminScanJourneyRecovery(slug) {
  const res = await authedFetch(`${API_BASE}/portal_admin_scan_journey_recovery_preview`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Recovery preview unavailable"), { status: res.status, body: data });
  return data;
}

export async function executeAdminScanJourneyRecovery(request) {
  const res = await authedFetch(`${API_BASE}/portal_admin_scan_journey_recovery`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || "Recovery unresolved"), { status: res.status, body: data });
  return data;
}

// Queue a hosted assessment. tenant_id is resolved server-side from the
// slug — never sent from here. On failure the thrown error carries
// status + parsed body (createAccessRequest pattern) so the 404/409
// data-gap responses can drive specific copy.
export async function startScanAssessment(slug, upn, tags = []) {
  const res = await authedFetch(`${API_BASE}/portal_scan_start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug, upn, tags }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Failed to start assessment: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json(); // { run_id, status_lookup_path, result_lookup_path }
}

// Persisted state for one run: { run_id, state, stage, message, ... }.
// Run-scoped fetch errors carry e.status (ADR-0085): the HTTP code must
// live on the error object, not only inside the message string, so
// pollRunStatus can tell a DETERMINISTIC failure (a persistent 404 from
// the ownership gate) from a transient one and stop early instead of
// masking it as a timeout. The createAccessRequest pattern, applied to
// the four run-scoped GETs.
function _scanFetchError(res, label) {
  const error = new Error(`${label}: ${res.status}`);
  error.status = res.status;
  return error;
}

export async function fetchScanStatus(runId) {
  const res = await authedFetch(`${API_BASE}/portal_scan_status?run_id=${encodeURIComponent(runId)}`);
  if (!res.ok) throw _scanFetchError(res, "Failed to fetch scan status");
  return res.json();
}

// Post-scan adjudication (ADR-0082 D2 wait experience): where a completed
// run's results are. {run_id, phase: "held"|"published"|"processing",
// stalled: bool} — run-scoped and customer-safe server-side (ownership via
// the same gate as fetchScanStatus; no hold reasons, no paths).
export async function fetchScanAdjudication(runId) {
  const res = await authedFetch(`${API_BASE}/portal_scan_adjudication?run_id=${encodeURIComponent(runId)}`);
  if (!res.ok) throw _scanFetchError(res, "Failed to fetch scan adjudication");
  return res.json();
}

// Paged run index, newest first: { runs: [...], next_cursor, top, has_more }.
// has_more is the engine's own answer to "is there another page", not an
// inference from the cursor. Pass `cursor` back verbatim — it is opaque.
// The thrown error carries status + parsed body (createAccessRequest
// pattern) — the Scans tab reads body.error === "scan_engine_not_configured"
// to show the connection-pending placeholder instead of error copy.
export async function fetchScanRuns({ top = 25, cursor = "" } = {}) {
  const params = new URLSearchParams({ top: String(top) });
  if (cursor) params.set("cursor", cursor);
  const res = await authedFetch(`${API_BASE}/portal_scan_runs?${params.toString()}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Failed to fetch scan runs: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

// Slugs with a scan drop currently queued in incoming/ (awaiting ingest).
// Returns { queued: { slug: count }, checked_at }. Fail-soft at the caller:
// any error means "nothing queued" (no badge) — this is a cosmetic signal.
export async function fetchScanIncomingStatus() {
  const res = await authedFetch(`${API_BASE}/portal_scan_incoming_status`);
  if (!res.ok) throw new Error(`Failed to fetch incoming status: ${res.status}`);
  return res.json();
}

// Full stored result document for a finished run (JSON, downloadable).
export async function fetchScanResult(runId) {
  const res = await authedFetch(`${API_BASE}/portal_scan_result?run_id=${encodeURIComponent(runId)}`);
  if (!res.ok) throw _scanFetchError(res, "Failed to fetch scan result");
  return res.json();
}

// Send a completed assessment into the scoring pipeline (backend fetches
// the engine's min blob and drops it into incoming/ for the 8-gate flow).
// On failure the thrown error carries status + parsed body so the row can
// render the 409/422/502 detail inline (createAccessRequest pattern).
// Returns { sent: true, slug, blob_path }.
export async function sendScanToScoring(runId) {
  const res = await authedFetch(`${API_BASE}/portal_scan_send_scoring`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ run_id: runId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Failed to send to scoring: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

// Recover a run id from an engine lookup path
// ('/api/getsecureauditstatus?runId=<id>') — the ack's most durable
// carrier of the id: queued acknowledgments have shipped without a RunId
// field, but the lookup paths always carry it in the query string. The
// paths are ENGINE-relative diagnostics that never resolve on the portal
// origin — STRING PARSE only, never fetched from the browser.
export function runIdFromLookupPath(path) {
  if (typeof path !== "string" || !/runid=/i.test(path)) return "";
  const query = path.split("?")[1] || "";
  for (const pair of query.split("&")) {
    const [k, v] = pair.split("=");
    if (k && k.toLowerCase() === "runid" && v) {
      try { return decodeURIComponent(v); } catch (err) { return v; }
    }
  }
  return "";
}

// Resolve the poll runId from a prereq-start acknowledgment, whatever its
// shape: our normalized camelCase contract, engine-style PascalCase, the
// legacy snake_case run_id, a bare-string runId (double-unwrap safety),
// or a paths-only ack (recovered by string-parsing the lookup paths).
// Returns "" when nothing usable is present. Exported for tests — this
// function ALONE decides whether the frontend can start polling, and the
// contract it enforces is: only the runId is needed; the lookup paths are
// diagnostics.
export function resolvePrereqAckRunId(ack) {
  if (typeof ack === "string") return ack.trim();
  if (!ack || typeof ack !== "object") return "";
  const direct = ack.runId || ack.RunId || ack.run_id;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const paths = [
    ack.statusLookupPath, ack.StatusLookupPath, ack.status_lookup_path,
    ack.runLookupPath, ack.RunLookupPath, ack.result_lookup_path,
  ];
  for (const p of paths) {
    const id = runIdFromLookupPath(p);
    if (id) return id;
  }
  return "";
}

// Start a prerequisites check (engine pre-flight; no assessment run).
// tenant_id is resolved server-side from the slug — never sent from here.
// Returns 202 { runId, queued } — our backend's browser contract. The
// engine's lookup paths stay server-side (logged for diagnostics, never
// sent); resolvePrereqAckRunId still tolerates older/odd ack shapes for
// deploy skew. All polling goes through the portal proxies keyed on
// runId alone — fetchScanStatus to a terminal state, then
// fetchScanPrereqResult.
// `upn` IS OPTIONAL FOR PATRIOT CALLERS (2026-08-14). Omit it and the
// backend resolves the scan account server-side with the same resolver and
// the same blocking codes the customer path uses — which is what lets the
// Scheduling / Scan Queue row button offer a one-click check without the
// operator picking an account first. The SecureScan365 tab still passes one
// explicitly and behaves exactly as before. The key is omitted rather than
// sent as undefined so the body carries no `upn` at all.
// `workflow: true` DECLARES A WORKFLOW ACT (2026-08-14) — this check was
// started from Amber's row button on Scheduling or on either Scan Queue
// section, and its outcome may move the row's workflow status. Omitted
// entirely otherwise, which is what the SecureScan365 tab's diagnostic
// Check Prerequisites button does: engineers run those for tenant-health
// reasons that have nothing to do with the review workflow, and they must
// not load the Scan Queue with work nobody requested. The backend records
// the declaration on the run's claim and both automation callers require
// it; a customer start can never set it (stripped server-side).
export async function startScanPrereqCheck(slug, upn, { workflow = false } = {}) {
  const res = await authedFetch(`${API_BASE}/portal_scan_prereq_start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      slug,
      ...(upn ? { upn } : {}),
      // Sent only when true, so the diagnostic call's body is byte-for-byte
      // what it was before this flag existed.
      ...(workflow ? { workflow: true } : {}),
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Failed to start prerequisites check: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  // Read the body EXACTLY once. A Response body is a one-shot stream: if
  // anything upstream already consumed it (an interceptor, a debugging
  // fetch tap that printed the body), json() rejects with a TypeError
  // ("body stream already read") even though the request succeeded and
  // the body was valid JSON. Name that precisely instead of letting it
  // masquerade as a failed start.
  try {
    return await res.json();
  } catch (parseErr) {
    const error = new Error(
      `Prerequisites check was accepted (HTTP ${res.status}) but its response body could not be read: ${parseErr?.message || parseErr}`
    );
    error.status = res.status;
    error.cause = parseErr;
    throw error;
  }
}

// Normalized Result.Prerequisites for a finished check: { outcome, coverage,
// items, unevaluated, issues, contract_version, result_available }. A Stale
// run comes back result_available:false + outcome "couldNotEvaluate" — not
// an error. An unexpected engine ContractVersion major surfaces as a 502.
export async function fetchScanPrereqResult(runId) {
  const res = await authedFetch(`${API_BASE}/portal_scan_prereq_result?run_id=${encodeURIComponent(runId)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Failed to fetch prerequisites result: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

// Stored license-inventory snapshot for one customer (Patriot-only admin
// Licensing tab). Never-collected is an EXPECTED 200 state:
// { available:false, reason:"never_collected", meta } — most customers
// read this until their next prerequisites check runs. A snapshot comes
// back { available:true, staleness, meta, collected_utc, source,
// inventory:{ products:[...] }, ... }.
export async function fetchLicenseInventory(slug) {
  const res = await authedFetch(`${API_BASE}/portal_license_inventory?slug=${encodeURIComponent(slug)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Failed to fetch license inventory: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

// A customer's OWN license inventory (customer Licensing page) — the
// allowlist projection, never the Patriot payload. Three 200 shapes:
// suppressed { available:false, reason:"not_available", message },
// { available:false, reason:"never_collected" }, or
// { available:true, staleness, meta, collected_utc, products:[...] }.
export async function fetchCustomerLicensing(slug) {
  const res = await authedFetch(`${API_BASE}/portal_customer_licensing?slug=${encodeURIComponent(slug)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Failed to fetch licensing: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

// ── Customer self-scan (Company Profile "Security Scan" card) ──
// Durable handoff clients. The state route is the sole durable projection
// authority. The initial 404/405 response is interpreted by the card as a
// mixed-version legacy fallback; every later error fails closed there.
export async function fetchCustomerScanState(slug, { asCustomer = false } = {}) {
  const params = new URLSearchParams({ slug });
  if (asCustomer) params.set("as_customer", "1");
  const res = await authedFetch(`${API_BASE}/portal_customer_scan_state?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || `Failed to fetch customer scan state: ${res.status}`);
    error.status = res.status;
    error.body = data;
    throw error;
  }
  return data;
}

export async function requestCustomerScan(slug, { asCustomer = false } = {}) {
  const body = { slug };
  if (asCustomer) body.as_customer = true;
  const res = await authedFetch(`${API_BASE}/portal_customer_scan_request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || `Failed to request customer scan: ${res.status}`);
    error.status = res.status;
    error.body = data;
    throw error;
  }
  return data;
}

// Same portal_scan_* proxies in CUSTOMER view. asCustomer=true adds the
// backend's Patriot-only opt-in (body as_customer / query as_customer=1)
// so an internal user demos the exact customer path — server-side
// scan-account resolution, in-flight lock, admission pre-gate, projected
// results, 24h scan cap. Real customers are always on the customer path
// and the flag is ignored server-side. Every thrown error carries
// status + parsed body (createAccessRequest pattern) — the card branches
// on body.error: prereq_in_flight / scan_in_flight (attach to the
// carried run id), scan_setup_incomplete (render message verbatim),
// scan_daily_cap (429 with next_available_utc).

export async function startCustomerPrereqCheck(slug, { asCustomer = false } = {}) {
  const body = { slug };
  if (asCustomer) body.as_customer = true;
  const res = await authedFetch(`${API_BASE}/portal_scan_prereq_start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || `Failed to start setup check: ${res.status}`);
    error.status = res.status;
    error.body = data;
    throw error;
  }
  // 202 {runId, queued} — or 200 {cached: true, age_seconds, ...projection}
  // when the backend served its short completed-result cache.
  return data;
}

export async function startCustomerScan(slug, { asCustomer = false } = {}) {
  const body = { slug };
  if (asCustomer) body.as_customer = true;
  const res = await authedFetch(`${API_BASE}/portal_scan_start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || `Failed to start scan: ${res.status}`);
    error.status = res.status;
    error.body = data;
    throw error;
  }
  return data; // 202 { run_id, queued } — no engine paths on the customer view
}

// Customer projection of a finished setup check: {runId, result_available,
// outcome, coverage, issues[]} — issues only (never Items), TenantAdmin
// audience only, already shaped server-side. Render what it sends.
export async function fetchCustomerPrereqResult(runId, { asCustomer = false } = {}) {
  const params = new URLSearchParams({ run_id: runId });
  if (asCustomer) params.set("as_customer", "1");
  const res = await authedFetch(`${API_BASE}/portal_scan_prereq_result?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || `Failed to fetch setup check result: ${res.status}`);
    error.status = res.status;
    error.body = data;
    throw error;
  }
  return data;
}

// Customer-view runs list (own tenant only, no cursor). Patriot callers
// must pass the slug with asCustomer; customers get their own implicitly.
export async function fetchCustomerScanRuns({ slug, asCustomer = false, top = 200 } = {}) {
  const params = new URLSearchParams({ top: String(top) });
  if (asCustomer) {
    params.set("as_customer", "1");
    params.set("slug", slug || "");
  }
  const res = await authedFetch(`${API_BASE}/portal_scan_runs?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(data.error || `Failed to fetch scan runs: ${res.status}`);
    error.status = res.status;
    error.body = data;
    throw error;
  }
  return data;
}

// Newest completed ASSESSMENT run's completed_utc from a customer-view
// runs list, or "" when none. Prereq-check runs carry no test catalog
// (total_tests null) and never count — mirrors the backend's 24h-cap
// derivation, which stays authoritative; this is display-only.
export function latestCompletedAssessmentUtc(runs) {
  let latest = "";
  let latestMs = -Infinity;
  for (const run of Array.isArray(runs) ? runs : []) {
    if (!run || !run.completed_utc || run.total_tests == null) continue;
    const ms = Date.parse(run.completed_utc);
    if (Number.isNaN(ms) || ms <= latestMs) continue;
    latest = run.completed_utc;
    latestMs = ms;
  }
  return latest;
}

// Buckets a customer-view runs list for the no-published-report empty
// state: "scan_running" when any run is in a non-terminal engine state,
// else "awaiting_publish" when a completed assessment exists (scored
// output just hasn't been published), else "no_scans" (nothing ever ran,
// or only setup checks / failed / cancelled runs — the next step is
// still to run one). Display-only, like latestCompletedAssessmentUtc:
// completed assessment = completed_utc + a test catalog (total_tests).
// A row with no recognizable state ("Unknown") deliberately does NOT
// count as running — a stuck "your scan is running" would suppress the
// run-your-first-scan prompt forever, while the reverse mistake is
// self-correcting (the backend's in-flight lock refuses a duplicate).
export function classifyNoDataScanState(runs) {
  const terminal = new Set(["Completed", "Failed", "Cancelled", "Stale", "Unknown"]);
  let running = false;
  let completedAssessment = false;
  for (const run of Array.isArray(runs) ? runs : []) {
    if (!run) continue;
    if (run.completed_utc && run.total_tests != null) {
      completedAssessment = true;
      continue;
    }
    const state = run.display_state || run.state || "";
    if (state && !terminal.has(state)) running = true;
  }
  if (running) return "scan_running";
  if (completedAssessment) return "awaiting_publish";
  return "no_scans";
}

// Creates a Pending access request. On failure the thrown error carries
// status + parsed body — the 409's error key and the 400's fields dict
// drive specific UI copy, so both must survive the throw.
export async function createAccessRequest(slug, { name, email, notes }) {
  const body = { name, email };
  if (notes) body.notes = notes;
  const res = await authedFetch(`${API_BASE}/access_list/${encodeURIComponent(slug)}/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `createAccessRequest failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Direct add by an Access Manager (customer RBAC) — no request/approve
// round trip. role is "member" (default) or "access_manager". Resolves
// with {status, entry, ...}: "active" (201, provisioned and live),
// "pending_review" (202, generic local part held for Patriot review), or
// "pending_manual" (202, a provisioning step failed — Patriot follows up;
// the row shows as Pending). Errors carry status + parsed body
// (createAccessRequest idiom); a 403 missing_capability means the caller
// isn't an Access Manager.
// suppressInvite is honored by the backend for PATRIOT callers only (a
// customer's request is silently ignored server-side, so the checkbox
// that sets it renders for internal users only). Sent only when true —
// an absent field is the unchanged send-the-invitation default.
export async function addAccessMember(slug, { name, email, role, notes, suppressInvite }) {
  const body = { name, email };
  if (role) body.role = role;
  if (notes) body.notes = notes;
  if (suppressInvite) body.suppress_invite_email = true;
  const res = await authedFetch(`${API_BASE}/access_list/${encodeURIComponent(slug)}/add`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `addAccessMember failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Change an Active member's Portal Role ("member" | "access_manager").
// Access Managers only (403 missing_capability otherwise); 409
// last_access_manager when the caller is the only manager demoting
// themself. Takes effect within ~2 minutes (roster verdict TTL). Returns
// the updated entry. Error carries status + parsed body.
export async function setAccessRole(slug, id, role) {
  const res = await authedFetch(`${API_BASE}/access_list/${encodeURIComponent(slug)}/role`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, role }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `setAccessRole failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Make a member the customer's ONE primary contact (comms designation,
// independent of role — a Member can be primary). Clears the prior
// primary in the same server-side operation. Access Managers only (403
// missing_capability); 409 not_active on non-Active rows. Returns the
// updated entry. Error carries status + parsed body.
export async function setAccessPrimary(slug, id) {
  return setContactFlag(slug, id, "primary");
}

// Make a member the customer's ONE executive contact — identical
// semantics and authorization to the technical contact (operator
// decision 2026-08-03: shared clear-then-set, do not fork).
export async function setAccessExecutive(slug, id) {
  return setContactFlag(slug, id, "executive");
}

async function setContactFlag(slug, id, kind) {
  const res = await authedFetch(`${API_BASE}/access_list/${encodeURIComponent(slug)}/${kind}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `set ${kind} contact failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Disables an Active roster member (their row flips to Removed). Access
// itself dies when the roster gate's cached verdict expires — within a few
// minutes, not the same second. Access Managers only (403
// missing_capability otherwise). Error carries status + parsed body
// (createAccessRequest idiom).
export async function disableAccessMember(slug, id) {
  const res = await authedFetch(`${API_BASE}/access_list/${encodeURIComponent(slug)}/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `disableAccessMember failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Approve a Pending access request (Patriot-only; 403 otherwise). Runs the
// Graph-first sequence server-side: invite as guest if absent (a REAL
// invitation email may be sent), add to the portal group, then flip the
// roster row Active. Returns {status:"approved", invited, already_member}.
// Failures carry status + parsed body; body.step names the failed step and
// every step is idempotent, so retrying is always safe.
export async function approveAccessRequest(slug, id) {
  const res = await authedFetch(`${API_BASE}/access_list/${encodeURIComponent(slug)}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `approveAccessRequest failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Deny a Pending access request (Patriot-only; 403 otherwise). Dataverse
// only — flips the row to Rejected with decision stamps; no invitation, no
// group change. reason is required (≤500 chars — pc_statusreason's max) and
// lands in the CUSTOMER-VISIBLE Status Reason shown under the roster badge.
// Returns {status:"denied"}. Error carries status + parsed body
// (createAccessRequest idiom).
export async function denyAccessRequest(slug, id, reason) {
  const res = await authedFetch(`${API_BASE}/access_list/${encodeURIComponent(slug)}/deny`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, reason }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `denyAccessRequest failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Escape user free-text before embedding in the ticket's HTML description body.
function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// Urgency label -> Freshservice priority (verified mapping; FS convention
// 1=Low, 2=Medium, 3=High, 4=Urgent). 4/Urgent is deliberately unreachable
// from the portal; agents escalate manually. Median-ticket calibration
// decided 2026-07-06.
const L3_URGENCY_PRIORITY = {
  "General Question / Request": 1,   // Low
  "Impacting Users": 2,              // Medium
  "Service Down / Security Risk": 3, // High
};

// Level 3 support / working-session / portal-support request. Builds the
// subject + HTML body and tags the ticket's patriot_service so it lands in
// the right dashboard pool: "Level 3 Support" (level3/working_session — rolls
// up on the L3 gauge) or "Portal Support" (portal_support — MUST match the FS
// choice value exactly; portal tickets never touch the gauges). kind is
// "level3" | "working_session" | "portal_support".
export async function createL3Ticket({ slug, requesterEmail, kind, categories, deployed, urgency, description }) {
  const cats = (categories || []).join(", ");
  const isPortal = kind === "portal_support";
  const label = kind === "working_session" ? "Working Session" : isPortal ? "Portal Support" : "Level 3";
  const subject = isPortal ? `Portal Support — ${cats}` : `${label} — ${cats} (${urgency})`;

  const sessionNote = kind === "working_session"
    ? "<p>This is a request for a dedicated working session. Scheduling a dedicated specialist may require additional lead time; your Patriot team will coordinate timing after review.</p>"
    : "";

  const body = `
<h3>${label} ${isPortal ? "Report" : "Request"}</h3>
${sessionNote}
<table>
  <tr><td><b>${isPortal ? "Problem:" : "Categories:"}</b></td><td>${escapeHtml(cats)}</td></tr>
  ${isPortal ? "" : `<tr><td><b>Currently deployed?</b></td><td>${escapeHtml(deployed)}</td></tr>`}
  <tr><td><b>Urgency:</b></td><td>${escapeHtml(urgency)}</td></tr>
</table>
<h4>Description</h4>
<p>${escapeHtml(description)}</p>
<hr>
<p><i>Submitted via the SecureShield 365 Dashboard</i></p>
`;

  return createTicket({
    client_id: slug,
    requester_email: requesterEmail || "",
    subject,
    description: body,
    patriot_service: isPortal ? "Portal Support" : "Level 3 Support",
    priority: L3_URGENCY_PRIORITY[urgency] || 1,
  });
}

// ── Ingestion ──
export async function triggerIngest(clientId, siteId, driveId, filePath) {
  const res = await authedFetch(`${API_BASE}/ingest_client`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: clientId,
      site_id: siteId,
      drive_id: driveId,
      file_path: filePath,
    }),
  });
  if (!res.ok) throw new Error(`Ingestion failed: ${res.status}`);
  return res.json();
}

export async function triggerBatchIngest(deleteAfter = false) {
  const res = await authedFetch(`${API_BASE}/batch_ingest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ delete_after: deleteAfter }),
  });
  if (!res.ok) throw new Error(`Batch ingest failed: ${res.status}`);
  return res.json();
}

// ── Status Overrides (Phase C — Dataverse-backed) ──
export async function setOverride(slug, settingId, body) {
  const res = await authedFetch(`${API_BASE}/overrides/${encodeURIComponent(slug)}/${settingId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`setOverride failed: ${res.status} ${errBody}`);
  }
  return res.json();
}

export async function clearOverride(slug, settingId, body) {
  const res = await authedFetch(`${API_BASE}/overrides/${encodeURIComponent(slug)}/${settingId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 404) return null;
    const errBody = await res.text();
    throw new Error(`clearOverride failed: ${res.status} ${errBody}`);
  }
  return res.json();
}

// ── Notes (Phase 1 — read-only, full transparency model) ──
export async function fetchNotes(slug) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/notes`);
  if (!res.ok) throw new Error(`fetchNotes failed: ${res.status}`);
  return res.json();
}

// ── Notes (Phase 4 — write/edit/delete) ──
export async function createNote(slug, note) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(note),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `createNote failed: ${res.status}`);
  }
  return res.json();
}

export async function updateNote(slug, noteId, fields) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/notes/${encodeURIComponent(noteId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `updateNote failed: ${res.status}`);
  }
  return res.json();
}

export async function deleteNote(slug, noteId) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/notes/${encodeURIComponent(noteId)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `deleteNote failed: ${res.status}`);
  }
  return res.json();
}

// ── Session Focus (Next Session queue — Dataverse-backed) ──
export async function fetchSessionFocus(slug) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/session-focus`);
  if (!res.ok) throw new Error(`Failed to fetch session focus: ${res.status}`);
  return res.json();
}

export async function createSessionFocusItem(slug, payload) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/session-focus`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(`Failed to add to session focus: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

export async function updateSessionFocusItem(slug, itemId, fields) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/session-focus/${encodeURIComponent(itemId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fields),
  });
  if (!res.ok) throw new Error(`Failed to update item: ${res.status}`);
  return res.json();
}

export async function deleteSessionFocusItem(slug, itemId) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/session-focus/${encodeURIComponent(itemId)}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 404) throw new Error(`Failed to delete item: ${res.status}`);
  return true;
}

export async function reorderSessionFocus(slug, orderedIds) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/session-focus/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
  if (!res.ok) throw new Error(`Failed to reorder: ${res.status}`);
  return res.json();
}

// ── Setting Reviews (PR 2 — Dataverse-backed review log) ──
export async function fetchReviews(slug, settingId) {
  try {
    const qs = settingId != null ? `?setting_id=${encodeURIComponent(settingId)}` : "";
    const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/reviews${qs}`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function createReview(slug, settingId, reviewedOn) {
  const body = { setting_id: settingId };
  if (reviewedOn) body.reviewed_on = reviewedOn;
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/reviews`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `createReview failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

export async function deleteReview(slug, reviewId) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/reviews/${encodeURIComponent(reviewId)}`, {
    method: "DELETE",
  });
  if (res.status === 204) return true;
  if (res.status === 404) return false;
  const err = await res.json().catch(() => ({}));
  throw new Error(err.error || `deleteReview failed: ${res.status}`);
}


// ── Staging gate: pending-scan review (Patriot-only endpoints) ──

// Patriot gets {pending, hold_reasons, executed_at, scored_at}; a customer
// asking about their OWN slug gets {pending, executed_at} only — the reason
// is an internal judgement about scan quality and is stripped server-side
// (2026-08-09). Any non-OK still degrades to "no banner": a customer
// outside their own slug, or a transient failure, must never render one.
export async function fetchPendingStatus(slug) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/pending-status`);
  if (!res.ok) {
    return { pending: false };
  }
  return res.json();
}

// Every scan this customer currently has held, newest first (Patriot-only).
// The per-customer companion to fetchReviewQueue, which is fleet-wide.
// Returns [] on any non-OK: a failed read must never be rendered as "no
// scans are held", so callers that care about the distinction check the
// queue instead. ADR-0088.
export async function fetchPendingList(slug) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/pending-list`);
  if (!res.ok) return [];
  const body = await res.json().catch(() => ({}));
  return Array.isArray(body?.pending) ? body.pending : [];
}

// pendingId (ADR-0088) selects WHICH held scan to load. Omitted means the
// newest, which is what this returned when a customer could only have one.
export async function fetchPendingScan(slug, pendingId) {
  const qs = pendingId ? `?pending_id=${encodeURIComponent(pendingId)}` : "";
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/pending${qs}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `fetchPendingScan failed: ${res.status}`);
  }
  return res.json();
}

// pendingId names the held scan to publish (ADR-0088). Omitted means the
// newest — the behaviour this had when a customer could only hold one, and
// what the pre-ADR-0088 callers relied on.
//
// A 409 is a REFUSAL, not a failure: the chosen scan is older than the one
// already published (D-a2). error.body carries the message that names the
// supported way to publish it anyway (un-publish first), so callers should
// render body.message rather than a generic failure string.
export async function publishPendingScan(slug, pendingId, publishRequestId = null) {
  if (!pendingId || !publishRequestId) throw new Error("Publish target and request ID required");
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/publish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      pending_id: pendingId,
      publish_request_id: publishRequestId,
      intent: "publish",
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `publishPendingScan failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// pendingId names the held scan to reject (ADR-0088). Omitted means the
// newest. Per-run only: sibling holds are untouched (D-b).
export async function rejectPendingScan(slug, pendingId) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/reject-pending`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(pendingId ? { pending_id: pendingId } : {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `rejectPendingScan failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}


// ── Compensating Control Packages (internal / Patriot-only endpoints) ──
// Apply a pre-built package (e.g. Proofpoint = 30 settings) to a customer in
// one action. The backend stamps each created override with the package id and
// derives the actor from the authenticated Patriot user, so no actor is passed
// from the client. Non-Patriot callers get 403 from these endpoints.

export async function listPackages() {
  const res = await authedFetch(`${API_BASE}/packages`);
  if (!res.ok) {
    // Non-Patriot users get 403 — treat as "no packages available".
    if (res.status === 403) return { packages: [] };
    throw new Error(`listPackages failed: ${res.status}`);
  }
  return res.json();
}

export async function previewPackage(packageId, slug) {
  const res = await authedFetch(
    `${API_BASE}/packages/${encodeURIComponent(packageId)}/preview/${encodeURIComponent(slug)}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `previewPackage failed: ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return res.json();
}

export async function applyPackage(packageId, slug) {
  const res = await authedFetch(
    `${API_BASE}/packages/${encodeURIComponent(packageId)}/apply/${encodeURIComponent(slug)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `applyPackage failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

export async function removePackage(packageId, slug) {
  const res = await authedFetch(
    `${API_BASE}/packages/${encodeURIComponent(packageId)}/apply/${encodeURIComponent(slug)}`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `removePackage failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// ── Recommendations Review completion (Patriot-only) ──
// Context for the Review Complete card/dialog: {customer, cycle|null,
// allowance{per_year,fallback}, reviews_used, no_recorded_reviews,
// last_completion|null, writes_enabled}. 422 = no active tracking row
// (a CRM problem the card surfaces verbatim). Errors carry status +
// parsed body (createAccessRequest idiom).
export async function fetchReviewCompletionContext(slug) {
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/review-completion`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `fetchReviewCompletionContext failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  return res.json();
}

// Record a completed (or no-show) recommendations review. The reviewer
// is always the signed-in clicker — the server stamps it; nothing
// identity- or cycle-shaped goes in the body. send_booking_link is
// no-show only and sent only when the operator explicitly chose —
// the two-radio choice in the dialog, never a default. click_date is
// the operator's LOCAL date (localDateString — same clock as the
// review-date default): the server anchors the forward schedule on it,
// validated to within one day of the server's UTC date; absent (older
// client) falls back server-side to the UTC date.
const COMPLETION_PENDING_MESSAGE =
  "Check the previous review completion result before making another change. Re-enter the original review details or ask staff to reconcile it.";
const COMPLETION_STORAGE_MESSAGE =
  "This browser cannot safely remember the review completion request.";
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const SHA256_HEX = /^[0-9a-f]{64}$/;
const ROW_GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function exactDateOnly(value) {
  if (typeof value !== "string" || !DATE_ONLY.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value;
}

function pendingCompletionSlot(slug) {
  const key = `ss365-review-completion:${slug}`;
  let raw;
  try { raw = window.sessionStorage.getItem(key); }
  catch (_) { throw new Error(COMPLETION_STORAGE_MESSAGE); }
  if (raw === null) return { key, pending: null, raw: null };
  let pending;
  try { pending = JSON.parse(raw); }
  catch (_) { throw new Error(COMPLETION_PENDING_MESSAGE); }
  if (!pending || typeof pending !== "object" || Array.isArray(pending) ||
    Object.keys(pending).sort().join(",") !== "clickDate,factDigest,requestId,version" ||
    pending.version !== 1 || typeof pending.requestId !== "string" ||
    !RETRY_UUID.test(pending.requestId) || !exactDateOnly(pending.clickDate) ||
    typeof pending.factDigest !== "string" || !SHA256_HEX.test(pending.factDigest) ||
    JSON.stringify(pending) !== raw) {
    throw new Error(COMPLETION_PENDING_MESSAGE);
  }
  return { key, pending, raw };
}

export function getPendingReviewCompletionDate(slug) {
  return pendingCompletionSlot(slug).pending?.clickDate || null;
}

// A same-tab second press cannot race a first press through the asynchronous
// fact digest, even if the first acknowledgement clears its durable slot.
// This set is only an in-flight exclusion; sessionStorage remains the
// recoverable request identity after an uncertain response or reload.
const completionInFlight = new Set();

export async function recordReviewCompletion(slug, facts) {
  if (completionInFlight.has(slug)) {
    throw new Error(COMPLETION_PENDING_MESSAGE);
  }
  completionInFlight.add(slug);
  try {
    return await recordReviewCompletionOnce(slug, facts);
  } finally {
    completionInFlight.delete(slug);
  }
}

async function recordReviewCompletionOnce(slug, { reviewDate, reviewType, note, sendBookingLink, clickDate, skipRequestedBy }) {
  const slot = pendingCompletionSlot(slug);
  const originalClickDate = slot.pending?.clickDate || clickDate || localDateString();
  if (!exactDateOnly(originalClickDate)) {
    throw new Error("The review completion date is invalid.");
  }
  const body = { review_date: reviewDate, review_type: reviewType,
    click_date: originalClickDate };
  if (note) body.note = note;
  if (reviewType === "no-show") body.send_booking_link = !!sendBookingLink;
  if (reviewType === "skipped") body.skip_requested_by = skipRequestedBy;
  if (typeof globalThis.crypto?.subtle?.digest !== "function") {
    throw new Error("This browser cannot verify the review completion request.");
  }
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256", new TextEncoder().encode(JSON.stringify(body)));
  const factDigest = Array.from(new Uint8Array(digest), value =>
    value.toString(16).padStart(2, "0")).join("");
  if (!SHA256_HEX.test(factDigest)) {
    throw new Error("This browser cannot verify the review completion request.");
  }
  let pending = slot.pending;
  let encoded = slot.raw;
  if (pending && pending.factDigest !== factDigest) {
    throw new Error(COMPLETION_PENDING_MESSAGE);
  }
  if (!pending) {
    // Digest is asynchronous. Another press may have stored its operation
    // while this call waited; it must not be replaced with a second UUID.
    if (pendingCompletionSlot(slug).raw !== slot.raw) {
      throw new Error(COMPLETION_PENDING_MESSAGE);
    }
    if (typeof globalThis.crypto?.randomUUID !== "function") {
      throw new Error("This browser cannot create a safe review completion request.");
    }
    const requestId = globalThis.crypto.randomUUID();
    if (!RETRY_UUID.test(requestId)) {
      throw new Error("This browser cannot create a safe review completion request.");
    }
    pending = { version: 1, requestId, clickDate: originalClickDate, factDigest };
    encoded = JSON.stringify(pending);
    try {
      window.sessionStorage.setItem(slot.key, encoded);
      if (window.sessionStorage.getItem(slot.key) !== encoded) {
        throw new Error("review completion request was not retained");
      }
    } catch (_) { throw new Error(COMPLETION_STORAGE_MESSAGE); }
  }
  try {
    if (window.sessionStorage.getItem(slot.key) !== encoded) {
      throw new Error("review completion request changed");
    }
  } catch (_) { throw new Error(COMPLETION_STORAGE_MESSAGE); }
  body.contract_request_id = pending.requestId;
  const res = await authedFetch(`${API_BASE}/clients/${encodeURIComponent(slug)}/review-completion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error || `recordReviewCompletion failed: ${res.status}`);
    error.status = res.status;
    error.body = err;
    throw error;
  }
  const result = await res.json();
  if (!result || typeof result !== "object" ||
    typeof result.completion?.id !== "string" ||
    !ROW_GUID.test(result.completion.id) ||
    result.completion.id.toLowerCase() === "00000000-0000-0000-0000-000000000000" ||
    result.completion.review_date !== body.review_date ||
    result.completion.review_type !== body.review_type) {
    throw new Error("The review result could not be verified. Check the previous result before trying again.");
  }
  try { window.sessionStorage.removeItem(slot.key); }
  catch (_) { /* Storage does not affect an acknowledged completion. */ }
  return result;
}

// ── Customer self-book surface (self-book-surface §3) ──
//
// ONE ROUTE, TWO VERBS. GET reads what the customer may see about their
// review allowance; POST asks for a review. The GET's payload is a
// server-built allow-list — a LEGACY customer receives {mode: "legacy"}
// and nothing else — and the frontend RENDERS it: no date arithmetic, no
// threshold comparison, no re-derivation of the balance (plan §9).
export async function fetchReviewBooking(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_review_booking/${encodeURIComponent(slug)}`
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(body.error || `Failed to fetch review booking: ${res.status}`);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

// The request body carries only an opaque retry ID. The server derives the
// customer and actor from the authenticated request, not this ID.
// The 409s ("a review request is already open" / "a review is already
// scheduled" / "no review allowance available" / freshness blocked) are the enforcement —
// their message is shown to the customer verbatim and the caller refetches,
// so the server's answer wins visibly (D-7).
const APPROVED_REVIEW_BOOKING_409_MESSAGES = new Set([
  "a review request is already open",
  "a booking link has already been sent",
  "a review is already scheduled",
  "no review allowance available",
  "Run an updated scan to enable booking.",
]);
const REVIEW_BOOKING_GENERIC_POST_ERROR =
  "We couldn't submit your request right now. Please try again later.";
const pendingReviewBookingRequests = new Map();

function reviewBookingRequestId(slug) {
  const key = `ss365-review-booking:${slug}`;
  let requestId = pendingReviewBookingRequests.get(key);
  if (!requestId) {
    try { requestId = window.sessionStorage.getItem(key); } catch (_) { /* unavailable */ }
  }
  if (!requestId) {
    if (typeof window.crypto?.randomUUID !== "function") {
      throw new Error(REVIEW_BOOKING_GENERIC_POST_ERROR);
    }
    requestId = window.crypto.randomUUID();
    try { window.sessionStorage.setItem(key, requestId); } catch (_) { /* unavailable */ }
  }
  pendingReviewBookingRequests.set(key, requestId);
  return requestId;
}

export async function requestReviewBooking(slug) {
  const requestId = reviewBookingRequestId(slug);
  const res = await authedFetch(
    `${API_BASE}/portal_review_booking/${encodeURIComponent(slug)}`,
    { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ requestId }) }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const approvedConflict =
      res.status === 409 && APPROVED_REVIEW_BOOKING_409_MESSAGES.has(body.error);
    const error = new Error(
      approvedConflict ? body.error : REVIEW_BOOKING_GENERIC_POST_ERROR
    );
    error.status = res.status;
    error.body = body;
    throw error;
  }
  const result = await res.json();
  const key = `ss365-review-booking:${slug}`;
  pendingReviewBookingRequests.delete(key);
  try { window.sessionStorage.removeItem(key); } catch (_) { /* unavailable */ }
  return result;
}

export async function requestReviewBookingAction(slug, action) {
  const res = await authedFetch(
    `${API_BASE}/portal_review_booking/${encodeURIComponent(slug)}/action`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(
      body.error || `Failed to open review booking action: ${res.status}`
    );
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

const ONBOARDING_BOOKING_GENERIC_ERROR =
  "Scheduling is temporarily unavailable. Please try again.";

export async function fetchOnboardingBooking(slug) {
  const res = await authedFetch(
    `${API_BASE}/portal_onboarding_booking/${encodeURIComponent(slug)}`
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(ONBOARDING_BOOKING_GENERIC_ERROR);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

export async function requestOnboardingBooking(slug, path) {
  const res = await authedFetch(
    `${API_BASE}/portal_onboarding_booking/${encodeURIComponent(slug)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path, origin: "completion" }),
    }
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error(ONBOARDING_BOOKING_GENERIC_ERROR);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return res.json();
}

// Internal request management deliberately uses its own server mode/capabilities.
async function autoRenewManagementResponse(res) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.error || "Auto-renew request management unavailable");
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return body;
}

export async function fetchAutoRenewRequests(filters = {}) {
  const query = new URLSearchParams();
  for (const key of ["status", "notification", "search", "continuation"]) {
    if (filters[key]) query.set(key, filters[key]);
  }
  return autoRenewManagementResponse(await authedFetch(
    `${API_BASE}/portal_auto_renew_requests?${query}`, { cache: "no-store" },
  ));
}

export async function fetchAutoRenewRequestDetail(requestId) {
  return autoRenewManagementResponse(await authedFetch(
    `${API_BASE}/portal_auto_renew_requests/${encodeURIComponent(requestId)}`, { cache: "no-store" },
  ));
}

export async function completeAutoRenewRequest(requestId, version) {
  return autoRenewManagementResponse(await authedFetch(
    `${API_BASE}/portal_auto_renew_requests/${encodeURIComponent(requestId)}/complete`,
    { method: "POST", cache: "no-store", headers: { "Content-Type": "application/json", "If-Match": version },
      body: JSON.stringify({ acknowledged: true }) },
  ));
}
